/**
 * 商品ページ（本文と JSON-LD）の組み立てと、その出来上がりのプレビュー。
 *
 * この面が守っていること:
 * - **見積もりの段を置かない。** この実行は LLM を呼ばないので、押す前に
 *   確かめるべき費用が無い（`faqGenerationEstimate` にあたる口も API に無い）
 * - **公開ではない。** 組み立てただけで、どこにも配信されていないと明示する
 * - 進捗を引き直す間隔は `Job.suggestedPollIntervalSec` に従い、画面が秒数を
 *   決め打ちしない。null（終端）になったら止める
 * - 進行中のジョブは**API に問い合わせて**拾い直す。拾い直しが解決するまで
 *   起動の導線を出さない（解決前の姿を見て「起動できる」と読み違えるため）
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  JobProgressDocument,
  LoadingState,
  Meter,
  PageGenerationJobsDocument,
  ProposeProductPageWritebackDocument,
  StartPageGenerationDocument,
  Tag,
  formatJstDateTime,
  type JobProgressQuery,
  type JobState,
  type ProductQuery,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  NOT_MEASURED,
  PAGE_IS_PREVIEW_ONLY_NOTE,
  PROPOSE_FROM_PAGE_NOTE,
} from "../api-capabilities";
import { JobControls } from "./JobControls";

type Job = NonNullable<JobProgressQuery["job"]>;
type GeneratedPage = NonNullable<NonNullable<ProductQuery["product"]>["page"]>;

const TERMINAL_STATES: readonly JobState[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

function isTerminal(state: JobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function count(value: number | null | undefined): string {
  return value == null ? NOT_MEASURED : value.toLocaleString("ja-JP");
}

/** 1 起動につき 1 つ。再送のたびに作り直すと二重に起動できてしまう */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function JobView({ job }: { job: Job }) {
  switch (job.state) {
    case "QUEUED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            順番を待っています
            <Tag tone="info">待機中</Tag>
          </div>
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            この画面を閉じても進みます。
          </p>
        </div>
      );

    case "RUNNING":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            ページを組み立てています（{count(job.succeeded)} /{" "}
            {count(job.total)}）<Tag tone="info">実行中</Tag>
          </div>
          <Meter ratio={job.progress} />
        </div>
      );

    case "SUCCEEDED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            ページを組み立てました
            <Tag tone="ok">完了</Tag>
          </div>
          <p style={{ margin: "4px 0 0" }}>
            作れた {count(job.succeeded)} 件。{PAGE_IS_PREVIEW_ONLY_NOTE}
          </p>
        </div>
      );

    case "SUCCEEDED_WITH_ERRORS":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            一部の商品でページを作れませんでした
            <Tag tone="warn">一部失敗</Tag>
          </div>
          <p style={{ margin: "4px 0 0" }}>
            作れた {count(job.succeeded)} 件 / 作れなかった {count(job.failed)}{" "}
            件。
          </p>
          {job.failedTargets.edges.length === 0 ? null : (
            <Disclosure summary="作れなかった理由">
              <ul className="vd-job-reasons">
                {job.failedTargets.edges.map(({ node }) => (
                  <li key={node.targetId}>
                    {node.label}: {node.reason}
                  </li>
                ))}
              </ul>
            </Disclosure>
          )}
        </div>
      );

    case "FAILED":
      return (
        <div className="vd-job vd-job--error" role="alert">
          <div className="vd-card-title">
            ページを組み立てられませんでした
            <Tag tone="danger">失敗</Tag>
          </div>
          <p style={{ margin: "4px 0 0" }}>
            {job.errorSummary ?? "理由を受け取れませんでした。"}
          </p>
        </div>
      );

    case "CANCELLED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            組み立ては中止されました
            <Tag>中止</Tag>
          </div>
        </div>
      );
  }
}

/**
 * 出来上がりのプレビュー。
 *
 * HTML はスクリプト・フォーム・親画面へのアクセスを許可しない iframe で
 * 描画する。本文ソースと JSON-LD も切り替えて確認できる。
 */
function PagePreview({ page }: { page: GeneratedPage }) {
  const [tab, setTab] = useState<"preview" | "body" | "jsonld">("preview");
  return (
    <>
      <div className="vd-row" style={{ marginBottom: 8 }}>
        <Button
          variant={tab === "preview" ? "primary" : undefined}
          onClick={() => setTab("preview")}
        >
          プレビュー
        </Button>
        <Button
          variant={tab === "body" ? "primary" : undefined}
          onClick={() => setTab("body")}
        >
          本文
        </Button>
        <Button
          variant={tab === "jsonld" ? "primary" : undefined}
          onClick={() => setTab("jsonld")}
        >
          JSON-LD
        </Button>
      </div>
      {tab === "preview" ? (
        <iframe
          title="生成した商品ページのプレビュー"
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={
            `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">` +
            page.body
          }
          style={{
            width: "100%",
            height: 720,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        />
      ) : (
        <pre className="vd-code" data-testid={`page-${tab}`}>
          {tab === "body" ? page.body : JSON.stringify(page.jsonld, null, 2)}
        </pre>
      )}
      <p className="vd-stat-note" style={{ marginBottom: 0 }}>
        載せた答え {count(page.faqCount)} 件 ・ 組み立て{" "}
        {formatJstDateTime(page.generatedAt)}
        {page.promptVersions.length === 0
          ? ""
          : ` ・ 版 ${page.promptVersions.join(" / ")}`}
      </p>
    </>
  );
}

export function PageGenerationPanel({
  productId,
  page,
  onFinished,
}: {
  productId: string;
  /** いま保存されているページ。組み立てていなければ null */
  page: GeneratedPage | null;
  /** 終わったら商品詳細を引き直す（出来上がりを反映するため） */
  onFinished: () => void;
}) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const idempotencyKey = useRef<string | null>(null);
  const notified = useRef<string | null>(null);

  const [runningResult] = useQuery({
    query: PageGenerationJobsDocument,
    variables: { state: "RUNNING" as JobState, productId },
    pause: jobId !== null,
  });
  const [queuedResult] = useQuery({
    query: PageGenerationJobsDocument,
    variables: { state: "QUEUED" as JobState, productId },
    pause: jobId !== null,
  });
  const [jobResult, refetchJob] = useQuery({
    query: JobProgressDocument,
    variables: { id: jobId ?? "" },
    pause: jobId === null,
    requestPolicy: "network-only" as const,
  });
  const [startResult, startPageGeneration] = useMutation(
    StartPageGenerationDocument,
  );
  const [proposeResult, proposeWriteback] = useMutation(
    ProposeProductPageWritebackDocument,
  );
  const [proposed, setProposed] = useState(false);

  const job = jobResult.data?.job ?? null;

  /** 拾い直しが解決するまでは起動の導線を出さない（FAQ 生成と同じ理由） */
  const lookingUp =
    jobId === null &&
    (runningResult.data === undefined || queuedResult.data === undefined) &&
    runningResult.error === undefined &&
    queuedResult.error === undefined;

  useEffect(() => {
    if (jobId !== null) return;
    const found =
      runningResult.data?.jobs.edges[0]?.node ??
      queuedResult.data?.jobs.edges[0]?.node ??
      null;
    if (found !== null) setJobId(found.id);
  }, [jobId, runningResult.data, queuedResult.data]);

  useEffect(() => {
    if (jobId === null || job === null) return;
    const intervalSec = job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) return;
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      refetchJob({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => clearTimeout(timer);
  }, [jobId, job, pollTick, refetchJob]);

  useEffect(() => {
    if (job === null || !isTerminal(job.state) || notified.current === job.id) {
      return;
    }
    notified.current = job.id;
    onFinished();
  }, [job, onFinished]);

  const start = useCallback(async () => {
    idempotencyKey.current ??= newIdempotencyKey();
    const response = await startPageGeneration({
      input: {
        productIds: [productId],
        idempotencyKey: idempotencyKey.current,
      },
    });
    const started = response.data?.startPageGeneration.job ?? null;
    if (started !== null) {
      setJobId(started.id);
      idempotencyKey.current = null;
    }
  }, [productId, startPageGeneration]);

  const reset = (): void => {
    setJobId(null);
    idempotencyKey.current = null;
    notified.current = null;
  };

  const userErrors = startResult.data?.startPageGeneration.userErrors ?? [];
  const proposeErrors =
    proposeResult.data?.proposeProductPageWriteback.userErrors ?? [];

  /**
   * 組み立てた本文を、EC の商品詳細の原稿として反映する提案にする。
   *
   * **提案を作るだけで、反映は走らない。** 承認は配信の画面で行い、
   * そこでも `APPROVED` で止まる（EC 側の口がつながっていない）。
   */
  const propose = async (): Promise<void> => {
    const response = await proposeWriteback({
      input: { productId, idempotencyKey: newIdempotencyKey() },
    });
    setProposed(
      (response.data?.proposeProductPageWriteback.proposal ?? null) !== null,
    );
  };

  return (
    <Card
      title="商品ページのプレビュー（本文と JSON-LD）"
      note={PAGE_IS_PREVIEW_ONLY_NOTE}
      extra={
        <span className="vd-row">
          {jobId === null && !lookingUp ? (
            <Button
              variant="primary"
              disabled={startResult.fetching}
              onClick={() => {
                void start();
              }}
            >
              {startResult.fetching
                ? "起動しています"
                : page === null
                  ? "ページを組み立てる"
                  : "組み立て直す"}
            </Button>
          ) : null}
          <HelpIcon slug="products/page-generation" />
        </span>
      }
    >
      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">
              組み立てを始められませんでした
            </span>
            {userErrors.map((error) => (
              <span key={`${error.code}-${error.message}`}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        {lookingUp ? (
          <LoadingState label="動いている組み立てがあるか確かめています" />
        ) : jobId !== null ? (
          jobResult.error !== undefined ? (
            <ErrorState
              title="進捗を読み込めませんでした"
              description={jobResult.error.message}
              onRetry={() => refetchJob({ requestPolicy: "network-only" })}
            />
          ) : job === null ? (
            <LoadingState label="進み具合を読み込んでいます" />
          ) : (
            <>
              <JobView job={job} />
              <JobControls
                job={job}
                onChanged={(next) => {
                  if (next.id !== job.id) {
                    notified.current = null;
                  }
                  setJobId(next.id);
                  refetchJob({ requestPolicy: "network-only" });
                }}
              />
              {isTerminal(job.state) ? (
                <div className="vd-row">
                  <Button onClick={reset}>閉じる</Button>
                </div>
              ) : null}
            </>
          )
        ) : null}

        {page === null ? (
          <EmptyState
            title="この商品のページはまだ組み立てていません"
            description="商品マスターの列と、配信中の答えから本文と JSON-LD を作ります。AI は呼びません。"
          />
        ) : (
          <>
            <PagePreview page={page} />
            {proposeErrors.length === 0 ? null : (
              <div className="vd-state vd-state--error" role="alert">
                <span className="vd-state-title">提案を作れませんでした</span>
                {proposeErrors.map((error) => (
                  <span key={`${error.code}-${error.message}`}>
                    {error.message}
                  </span>
                ))}
              </div>
            )}
            {proposed ? (
              <div className="vd-notice" role="status">
                提案を作りました。承認は
                <Link to="/publish">配信</Link>
                の「反映の提案」から行います。承認しても、EC
                側の口がつながるまで反映は走りません。
              </div>
            ) : null}
            <div className="vd-row">
              <Button
                disabled={proposeResult.fetching}
                onClick={() => {
                  void propose();
                }}
              >
                {proposeResult.fetching ? "提案しています" : "公開を提案する"}
              </Button>
            </div>
            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              {PROPOSE_FROM_PAGE_NOTE}
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
